import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useNotifications, useMarkAllRead } from "../../../hooks/useNotifications";
import { NotificationRow } from "./NotificationRow";
import { SeenBatcherProvider } from "./SeenBatcher";
import { CheckCheck, Settings, Loader2 } from "lucide-react";
import { trackEvent } from "../../../utils/analytics";

const NotificationDropdownInner = ({ onClose }) => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  
  // Assuming user is authenticated if they can see the dropdown
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = useNotifications({ 
    unreadOnly, 
    isAuthenticated: true 
  });
  
  const { mutate: markAllRead } = useMarkAllRead();

  const handleMarkAllRead = () => {
    trackEvent("notifications_marked_all_read", { source: "dropdown" });
    markAllRead();
  };

  const notifications = data?.pages.flatMap((page) => page.data) || [];

  return (
    // Mobile: a fixed, viewport-anchored sheet under the navbar — the previous
    // `absolute right-0 w-80`, positioned relative to the small bell-icon
    // wrapper, didn't fit a narrow screen and left page content visible
    // around it instead of spanning properly. Desktop keeps the original
    // dropdown anchored to the bell, unchanged.
    <div className="fixed inset-x-3 top-16 max-h-[calc(100vh-5rem)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 md:w-96 sm:max-h-[85vh] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <h3 className="font-bold text-gray-900">Notifications</h3>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleMarkAllRead}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
            title="Mark all as read"
          >
            <CheckCheck size={14} />
            <span className="hidden sm:inline">Mark all read</span>
          </button>
          <Link 
            to="/settings/notifications" 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors"
          >
            <Settings size={16} />
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b border-gray-100 flex gap-2">
        <button
          onClick={() => setUnreadOnly(false)}
          className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
            !unreadOnly ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          All
        </button>
        <button
          onClick={() => setUnreadOnly(true)}
          className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
            unreadOnly ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Unread
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1 overscroll-contain">
        {isLoading && (
          <div className="p-8 flex justify-center text-gray-400">
            <Loader2 className="animate-spin w-6 h-6" />
          </div>
        )}

        {isError && (
          <div className="p-8 text-center text-sm text-red-500">
            Couldn't load notifications. Try again.
          </div>
        )}

        {!isLoading && !isError && notifications.length === 0 && (
          <div className="p-10 text-center flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
              <CheckCheck className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-gray-500 text-sm font-medium">
              {unreadOnly ? "No unread notifications." : "You're all caught up."}
            </p>
            {!unreadOnly && (
              <p className="text-gray-400 text-xs mt-1">New activity will appear here.</p>
            )}
          </div>
        )}

        {!isLoading && !isError && notifications.length > 0 && (
          <div className="flex flex-col">
            {notifications.map((notif) => (
              <NotificationRow 
                key={notif.id} 
                notification={notif} 
                onClickCallback={onClose} 
              />
            ))}
            
            {/* Load More */}
            {hasNextPage && (
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="p-3 w-full text-center text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50"
              >
                {isFetchingNextPage ? "Loading..." : "Load older"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer Link */}
      <div className="border-t border-gray-100 p-2">
        <Link
          to="/notifications"
          onClick={onClose}
          className="block w-full text-center py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
};

// The provider wraps the dropdown rather than living inside it, so its unmount
// cleanup still runs — and flushes anything seen in the last moment before the
// panel closed — after the list itself has gone.
export const NotificationDropdown = (props) => (
  <SeenBatcherProvider>
    <NotificationDropdownInner {...props} />
  </SeenBatcherProvider>
);
